import cx from "classnames";
import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import type { ReactNode } from "react";
import { SanitizeFieldValue } from "@/appComponents/Blocks/Cards/RecordInfoCard/utils";
import { AgentHeadingProfileCard } from "./AgentHeadingProfileCard";
import { getDisplayMap } from "../getDisplayMap";
import { getMainDisplayFieldIds } from "@/Utils/getMainDisplayFieldIds";
import { kAgentDefaultPhoto } from "@coreapp/constants";
import { useRecordPhotoSrc } from "@/hooks/useRecordPhotoSrc";
import { AgentLabelValueRow, AgentStatTile } from "./AgentStatTile";
import { AgentAlertList } from "./AgentAlertList";
import {
  AGENT_CARD_SECTIONS,
  AgentCardSections,
  AgentHeadingConfig,
  AgentSlotId,
  AgentSlotIds,
  getAgentSlotFieldName,
  getAgentSlotPercentField,
  getVisibleAgentSlots,
  isAgentSectionVisible,
  resolveAgentAlerts,
  sanitizeAgentLinkValue
} from "./AgentProfile.constants";

export type AgentCardProps = {
  record: Record<string, any>;
  agentConfig: AgentHeadingConfig | null | undefined;
  includedSlotIds: string[] | null;
  /** From getAvailableAgentSlots; null ⇒ metadata unknown, no gating. */
  availableSlotIds?: Set<string> | null;
  showMoreFields: boolean | null;
  /** Compatible with both boolean and boolean|null useState setters. */
  setShowMoreFields: (updater: (prev: boolean | null) => boolean) => void;
};

const EXPANDED_SECTIONS = [
  AgentCardSections.Lifecycle,
  AgentCardSections.Performance,
  AgentCardSections.Adoption,
  AgentCardSections.Cost,
  AgentCardSections.Alerts,
  AgentCardSections.ActionBreakdown
];

/** Numeric parse for display strings like "3,420" or "128 hrs"; null when non-numeric. */
const parseDisplayNumber = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const AgentCardSection = ({
  title,
  titleRight,
  children,
  dataTestId
}: {
  title: string;
  titleRight?: ReactNode;
  children: ReactNode;
  dataTestId: string;
}) => {
  return (
    <Flex
      direction="column"
      gap="8px"
      className="ocn-agent-profile-section"
      dataTestId={dataTestId}
    >
      <Flex direction="row" justifyContent="space-between" alignItems="center" gap="8px">
        <Text size="small" className="oc-font-weight-500">
          {title}
        </Text>
        {titleRight}
      </Flex>
      {children}
    </Flex>
  );
};

const ExternalTextLink = ({
  href,
  label,
  dataTestId
}: {
  href: string;
  label: string;
  dataTestId: string;
}) => {
  return (
    <a
      className="ocn-agent-profile-text-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={dataTestId}
    >
      {label}
    </a>
  );
};

export const AgentBaseballCard = (props: AgentCardProps) => {
  const {
    record,
    agentConfig,
    includedSlotIds,
    availableSlotIds,
    showMoreFields,
    setShowMoreFields
  } = props;

  const visibleSlots = getVisibleAgentSlots(includedSlotIds, availableSlotIds);

  const mainFieldIds = getMainDisplayFieldIds(false, "Holder");
  const photoFieldName = getDisplayMap(mainFieldIds, "Box", false).photo;
  const photoSrc = useRecordPhotoSrc(
    photoFieldName ? record?.[photoFieldName] : undefined,
    kAgentDefaultPhoto
  );

  const readSlot = (slotId: AgentSlotId) => record?.[getAgentSlotFieldName(agentConfig, slotId)];
  const displaySlot = (slotId: AgentSlotId) => SanitizeFieldValue(readSlot(slotId), null, "--");
  // Link slots feed hrefs: blocked sentinels and unsafe schemes resolve to ""
  // (link hidden) rather than rendering a masked-but-clickable anchor.
  const linkSlot = (slotId: AgentSlotId): string => sanitizeAgentLinkValue(readSlot(slotId));

  const isVisible = (slotId: AgentSlotId) => visibleSlots.has(slotId);
  const sectionVisible = (section: AgentCardSections) =>
    isAgentSectionVisible(section, visibleSlots);

  const hasExpandableSections = EXPANDED_SECTIONS.some(sectionVisible);

  const renderStatTile = (slotId: AgentSlotId, caption: string) => {
    if (!isVisible(slotId)) {
      return null;
    }
    return (
      <AgentStatTile
        value={displaySlot(slotId)}
        caption={caption}
        dataTestId={`agent-stat-${getAgentSlotFieldName(agentConfig, slotId)}`}
      />
    );
  };

  const renderLabelValueRow = (slotId: AgentSlotId, label: string) => {
    if (!isVisible(slotId)) {
      return null;
    }
    return <AgentLabelValueRow label={label} value={displaySlot(slotId)} />;
  };

  const renderAboutSection = () => {
    if (!sectionVisible(AgentCardSections.About)) {
      return null;
    }
    const detailLink = linkSlot(AgentSlotIds.detailLink);
    return (
      <AgentCardSection
        title={appstr("About me")}
        dataTestId="agent-card-section-about"
        titleRight={
          isVisible(AgentSlotIds.detailLink) && detailLink ? (
            <ExternalTextLink
              href={detailLink}
              label={appstr("Additional details")}
              dataTestId="agent-card-detail-link"
            />
          ) : undefined
        }
      >
        {isVisible(AgentSlotIds.about) && (
          <Text size="small" className="ocn-agent-profile-about-text">
            {displaySlot(AgentSlotIds.about)}
          </Text>
        )}
        <Flex direction="column" className="ocn-agent-profile-rows">
          {renderLabelValueRow(AgentSlotIds.lastUpdated, appstr("Last updated"))}
          {renderLabelValueRow(AgentSlotIds.reviewFrequency, appstr("Review frequency"))}
          {renderLabelValueRow(AgentSlotIds.modelUsed, appstr("Models used"))}
          {renderLabelValueRow(AgentSlotIds.platform, appstr("Platform"))}
        </Flex>
      </AgentCardSection>
    );
  };

  const renderLifecycleSection = () => {
    if (!sectionVisible(AgentCardSections.Lifecycle)) {
      return null;
    }
    const auditLink = linkSlot(AgentSlotIds.auditLogLink);
    return (
      <AgentCardSection
        title={appstr("Lifecycle and governance details")}
        dataTestId="agent-card-section-lifecycle"
      >
        <Flex direction="column" className="ocn-agent-profile-rows">
          {renderLabelValueRow(AgentSlotIds.dateCreated, appstr("Date created"))}
          {renderLabelValueRow(AgentSlotIds.dateDeployed, appstr("Date deployed"))}
          {renderLabelValueRow(AgentSlotIds.accessScope, appstr("Access scope"))}
          {isVisible(AgentSlotIds.auditLogLink) && (
            <AgentLabelValueRow
              label={appstr("Agent audit log")}
              value="--"
              valueContent={
                auditLink ? (
                  <ExternalTextLink
                    href={auditLink}
                    label={appstr("view here")}
                    dataTestId="agent-card-audit-log-link"
                  />
                ) : undefined
              }
            />
          )}
        </Flex>
      </AgentCardSection>
    );
  };

  const renderPerformanceSection = () => {
    if (!sectionVisible(AgentCardSections.Performance)) {
      return null;
    }
    return (
      <AgentCardSection title={appstr("Performance")} dataTestId="agent-card-section-performance">
        <div className="ocn-agent-stat-grid">
          {renderStatTile(AgentSlotIds.errorsPrevented, appstr("Errors prevented"))}
          {renderStatTile(AgentSlotIds.autoCorrections, appstr("Auto-corrections"))}
          {renderStatTile(AgentSlotIds.timeSaved, appstr("Time saved (this month)"))}
          {renderStatTile(AgentSlotIds.itemsReviewed, appstr("Items reviewed (last pay cycle)"))}
        </div>
      </AgentCardSection>
    );
  };

  const renderAdoptionSection = () => {
    if (!sectionVisible(AgentCardSections.Adoption)) {
      return null;
    }
    const daysSinceLastUse = parseDisplayNumber(readSlot(AgentSlotIds.daysSinceLastUse));
    const lastRanCaption =
      isVisible(AgentSlotIds.daysSinceLastUse) && daysSinceLastUse != null
        ? appstr("last ran {n} days ago", { n: daysSinceLastUse })
        : null;
    return (
      <AgentCardSection
        title={appstr("Adoption & activity")}
        dataTestId="agent-card-section-adoption"
        titleRight={
          lastRanCaption ? (
            <Text size="xsmall" colorModifier="grey">
              {lastRanCaption}
            </Text>
          ) : undefined
        }
      >
        <div className="ocn-agent-stat-grid">
          {renderStatTile(AgentSlotIds.activeUsers, appstr("Active users"))}
          {renderStatTile(AgentSlotIds.sessions, appstr("Sessions (this month)"))}
          {renderStatTile(AgentSlotIds.requestsMade, appstr("Requests made"))}
          {renderStatTile(AgentSlotIds.approvalRate, appstr("Approval rate"))}
        </div>
      </AgentCardSection>
    );
  };

  const renderCostSection = () => {
    if (!sectionVisible(AgentCardSections.Cost)) {
      return null;
    }
    return (
      <AgentCardSection title={appstr("Cost")} dataTestId="agent-card-section-cost">
        <div className="ocn-agent-stat-grid">
          {renderStatTile(AgentSlotIds.agentSpend, appstr("Agent spend"))}
          {renderStatTile(AgentSlotIds.tokenSpend, appstr("Token spend (this month)"))}
        </div>
      </AgentCardSection>
    );
  };

  const renderAlertsSection = () => {
    if (!sectionVisible(AgentCardSections.Alerts)) {
      return null;
    }
    const alertsData = resolveAgentAlerts(record, agentConfig);
    const countValue = (count: number | null | undefined) => (count == null ? "--" : String(count));
    return (
      <AgentCardSection title={appstr("Alerts")} dataTestId="agent-card-section-alerts">
        <div className="ocn-agent-stat-grid">
          {isVisible(AgentSlotIds.openAlerts) && (
            <AgentStatTile
              value={countValue(alertsData?.openAlerts)}
              caption={appstr("Open alerts")}
              dataTestId="agent-stat-open-alerts"
            />
          )}
          {isVisible(AgentSlotIds.criticalAlerts) && (
            <AgentStatTile
              value={countValue(alertsData?.criticalAlerts)}
              caption={appstr("Critical alerts")}
              dataTestId="agent-stat-critical-alerts"
            />
          )}
        </div>
        {isVisible(AgentSlotIds.topAlerts) && alertsData && (
          <AgentAlertList alerts={alertsData.topAlerts} />
        )}
      </AgentCardSection>
    );
  };

  const renderActionBreakdownSection = () => {
    if (!sectionVisible(AgentCardSections.ActionBreakdown)) {
      return null;
    }
    // Percentages come from isolved's own actionBreakdown_*_percentage columns
    // and are rendered verbatim, per slot. We deliberately do NOT derive a share
    // from the counts: the percentage is isolved's metric to define, and
    // recomputing it would hide any error in their data behind a number that
    // always sums to 100 while disagreeing with their own Guardian UI.
    const breakdownSlots = AGENT_CARD_SECTIONS[AgentCardSections.ActionBreakdown].filter(isVisible);
    const captions: Record<string, string> = {
      [AgentSlotIds.autoApplied]: appstr("Auto-applied"),
      [AgentSlotIds.approved]: appstr("Approved"),
      [AgentSlotIds.resolved]: appstr("Resolved"),
      [AgentSlotIds.dismissed]: appstr("Dismissed")
    };
    return (
      <AgentCardSection
        title={appstr("Action breakdown")}
        dataTestId="agent-card-section-action-breakdown"
      >
        <div className="ocn-agent-stat-grid">
          {breakdownSlots.map(slotId => {
            const percentField = getAgentSlotPercentField(slotId);
            const pushedPercent = percentField ? parseDisplayNumber(record?.[percentField]) : null;
            const percent = pushedPercent != null ? `${pushedPercent}%` : undefined;
            return (
              <AgentStatTile
                key={slotId}
                value={displaySlot(slotId)}
                caption={captions[slotId]}
                percent={percent}
                captionFirst
                dataTestId={`agent-stat-${getAgentSlotFieldName(agentConfig, slotId)}`}
              />
            );
          })}
        </div>
      </AgentCardSection>
    );
  };

  return (
    <>
      <AgentHeadingProfileCard
        record={record}
        config={agentConfig}
        visibleSlots={visibleSlots}
        photoSrc={photoSrc}
      />
      <div className={cx("ocn-profile-card-content", "ocn-list-scrollbar", "ocn-agent-card-body")}>
        {renderAboutSection()}
        {showMoreFields && (
          <>
            {renderLifecycleSection()}
            {renderPerformanceSection()}
            {renderAdoptionSection()}
            {renderCostSection()}
            {renderAlertsSection()}
            {renderActionBreakdownSection()}
          </>
        )}
        {hasExpandableSections && (
          <div className="ocn-profile-card-additional">
            <Button
              dataTestId="profile-card-more-toggle"
              // `?? true` keeps the preview modal's initial null state (rendered
              // expanded there) collapsing on the first click.
              onClick={() => setShowMoreFields(prevShowMoreFields => !(prevShowMoreFields ?? true))}
              size="small"
              modifier="tertiary"
              iconProps={{
                name: showMoreFields ? "square-minus" : "square-plus",
                size: "small"
              }}
            >
              {showMoreFields ? appstr("Show less") : appstr("Show more")}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
